import { computed, ref, toRef, watch, inject} from "vue";
import type { Component } from "vue";
import { useFetch } from "@/helpers/composables/useFetch";
import ImageUpdateListItem from "@/components/ImageUpdateListItem.vue";
import StockUpdatesListItem from "@/components/StockUpdatesListItem.vue";
import NewListItem from "@/components/NewListItem.vue";
import ProductUpdateListItem from "@/components/ProductUpdateListItem.vue";
import MapUpdateListItem from "@/components/MapUpdateListItem.vue";
// import { route } from "ziggy-js";
import { v4 as uuidv4 } from 'uuid';
export interface ItemFeedResponse {
    data: any[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface ItemFeedTab {
    value: string;
    label: string;
    rowComponent: Component;
    url: string;
}

export function useItemFeed() {

    let route = inject('route');
    const tabs: ItemFeedTab[] = [
        {
            value: 'newly-added',
            label: "Newly Added",
            rowComponent: NewListItem,
            url: route("itemFeed.newProducts")
        },
        {
            value: 'product-updates',
            label: "Product Updates",
            rowComponent: ProductUpdateListItem,
            url: route("itemFeed.productLogs")
        },
        {
            value: 'map-updates',
            label: "MAP Updates",
            rowComponent: MapUpdateListItem,
            url: route("itemFeed.mapUpdates")
        },
        {
            value: 'image-updates',
            label: "Image Updates",
            rowComponent: ImageUpdateListItem,
            url: route("itemFeed.imageUpdates")
        },
        {
            value: 'stock-updates',
            label: "Stock Updates",
            rowComponent: StockUpdatesListItem,
            url: route("itemFeed.instockUpdates")
        }
    ];

    const activeTab = ref<string>('newly-added');

    const activeTabData = computed(() =>
        tabs.find((tab) => tab.value === activeTab.value)
    );
    const currentPage = ref(1);

    const dataFilters = ref({});
    const applyFilters = (filters) => {
        dataFilters.value = filters
    };

    const isActive = computed(
        () => (tab: ItemFeedTab) => activeTab.value.label === tab.label
    );

    const dataUrl = computed(() => {
        const currentUrl = new URL(activeTabData.value.url);
        currentUrl.search = new URLSearchParams({
            ...dataFilters.value,
            page: currentPage.value?.toString()
        }).toString();
        return currentUrl.toString();
    });

    const { data:response, execute:fetchItemFeedData } = useFetch<ItemFeedResponse>(dataUrl, {
        refetch: true,
        updateDataOnError: true,
        immediate: false,
        beforeFetch(ctx) {
            ctx.options.headers = {
                ...ctx.options.headers,
                'Accept': 'application/json',
            }
            return ctx;
        },
        onFetchError(ctx) {
            if(ctx?.response?.status != 200){
                ctx.data = {
                    data: [],
                    meta: {
                        current_page: 1,
                        last_page: 1,
                        per_page: 1,
                        total: 0
                    }
                };
            }
            return ctx;
        },
        afterFetch(ctx) {
            loading.value = false;
            return ctx;
        }
    })
    .get()
    .json();

    watch(
        dataUrl,
        () => {
            loading.value = true;
            fetchItemFeedData()
        }
    );
    const itemFeedResponse = computed(()=>{
        const data = response.value?.data;
        const meta = response.value?.meta;
        return {
            ...data,
                data: data?.map?.((item) => ({
                    ...item,
                    uuid: uuidv4()
                })) ?? [],
                meta: meta
        }
    })
    const loading = ref(false);
    const isLoading = computed(()=>{
        return loading.value == true || (response.isFetching == true);
    })

    const reportUrl = computed(() => {

        const map = {
            'newly-added': 'new-products',
            'product-updates': 'product-logs',
            'map-updates': 'map-update',
            'image-updates': 'image-update',
            'stock-updates': 'instock-update',
        }
        const params = {
            report_options: {
                type: map[activeTab.value],
                file_type: 'csv',
            },
            limit: 1000,
            filters: {
                ...dataFilters.value
            }
        }
        return route('endpoint.report.download', params);
    })
    const { data: reportData, execute: downloadReport } = useFetch(reportUrl, {
        afterFetch(ctx){
            const url = window.URL.createObjectURL(ctx.data); // Create a download link from blob
            const a = document.createElement('a');
            a.href = url;
            
            a.download = `${activeTabData?.value?.label}`; // Specify the filename for download
            document.body.appendChild(a);
            a.click();
            a.remove(); // Remove the te
            return ctx;
        },
        immediate: false,
    }).get().blob();
    return {
        tabs,
        activeTab,
        isActive,
        currentPage,
        fetchItemFeedData,
        applyFilters,
        loading,
        isFetching: response.isFetching,
        isLoading,
        error: response.error,
        itemFeedResponse,
        activeTabData,
        dataUrl,
        dataFilters,
        reportData,
        reportUrl,
        downloadReport
    };
}
